import { READY_PLAYER_ME_CONFIG, getAvatarCreationUrl, getAvatarUrl } from '../config/readyPlayerMe';
import { UserAvatar } from '../types';

export class ReadyPlayerMeService {
  /**
   * Open the Ready Player Me avatar creation interface
   * @param userId - The user's ID
   * @param redirectUrl - URL to redirect to after avatar creation
   */
  static openAvatarCreator(userId: string, redirectUrl?: string): void {
    // Add callback parameters to the redirect URL
    const callbackUrl = redirectUrl ? 
      `${redirectUrl}?callback=readyplayer&userId=${userId}` : 
      `${window.location.origin}/profile?callback=readyplayer&userId=${userId}`;
    
    const avatarCreationUrl = getAvatarCreationUrl(userId, callbackUrl);
    
    // Open in a new window/tab
    const newWindow = window.open(
      avatarCreationUrl,
      'ready-player-me-avatar-creator',
      'width=800,height=600,scrollbars=yes,resizable=yes'
    );
    
    if (!newWindow) {
      throw new Error('Failed to open avatar creator. Please allow popups for this site.');
    }
    
    // Focus the new window
    newWindow.focus();
    
    // Set up a listener for when the window closes or redirects
    const checkClosed = setInterval(() => {
      if (newWindow.closed) {
        clearInterval(checkClosed);
        // Window was closed, user might have completed avatar creation
        // We'll handle the callback when they return to the main window
      }
    }, 1000);
  }
  
  /**
   * Get avatar URL for display
   * @param avatarId - The Ready Player Me avatar ID
   * @param options - Display options
   */
  static getAvatarDisplayUrl(avatarId: string, options?: {
    size?: number;
    quality?: 'low' | 'medium' | 'high';
    backgroundColor?: string;
    format?: 'glb' | 'png' | 'jpg';
  }): string {
    return getAvatarUrl(avatarId, { format: 'glb', ...options });
  }
  
  /**
   * Convert Ready Player Me avatar data to our UserAvatar format
   * @param avatarData - Data from Ready Player Me
   * @param userId - User ID
   * @param teamId - Team ID
   */
  static convertToUserAvatar(
    avatarData: {
      avatarId: string;
      avatarUrl: string;
      thumbnailUrl?: string;
      metadata?: any;
    },
    userId: string,
    teamId: string
  ): Partial<UserAvatar> {
    return {
      userId,
      teamId,
      // Store the Ready Player Me avatar ID
      readyPlayerMeAvatarId: avatarData.avatarId,
      // Store the avatar URL
      avatarUrl: avatarData.avatarUrl,
      // Store thumbnail if available
      thumbnailUrl: avatarData.thumbnailUrl,
      // Store metadata only if it exists
      ...(avatarData.metadata && { metadata: avatarData.metadata }),
      // Set default customization values
      skinTone: '#FDBCB4',
      hairColor: '#8B4513',
      eyeColor: '#4A90E2',
      // No equipped items initially
      hat: undefined,
      shirt: undefined,
      pants: undefined,
      shoes: undefined,
      accessory: undefined,
      background: undefined,
    };
  }
  
  /**
   * Validate Ready Player Me avatar URL
   * @param avatarUrl - The avatar URL to validate
   */
  static isValidAvatarUrl(avatarUrl: string): boolean {
    try {
      const url = new URL(avatarUrl);
      return url.hostname.includes('readyplayer.me') || url.hostname.includes('wakarunning.readyplayer.me');
    } catch {
      return false;
    }
  }
  
  /**
   * Extract avatar ID from Ready Player Me URL
   * @param avatarUrl - The avatar URL
   */
  static extractAvatarId(avatarUrl: string): string | null {
    try {
      const url = new URL(avatarUrl);
      const pathParts = url.pathname.split('/');
      const avatarId = pathParts[pathParts.length - 1].replace('.glb', '');
      return avatarId;
    } catch {
      return null;
    }
  }

  /**
   * Open avatar creator with locked assets configuration
   * @param userId - The user's ID
   * @param redirectUrl - URL to redirect to after avatar creation
   * @param lockedAssets - Array of asset IDs that should be locked for this user
   */
  static openAvatarCreatorWithAssets(
    userId: string, 
    redirectUrl?: string,
    lockedAssets?: string[]
  ): void {
    // Create a callback URL that ReadyPlayerMe will redirect to
    const baseCallbackUrl = redirectUrl || `${window.location.origin}/profile`;
    const callbackUrl = `${baseCallbackUrl}?callback=readyplayer&userId=${userId}`;
    
    // Use ReadyPlayerMe's iframe approach with frameApi
    let avatarCreationUrl = `${READY_PLAYER_ME_CONFIG.baseUrl}/avatar?frameApi&userId=${userId}`;
    
    // Add locked assets parameter if provided
    if (lockedAssets && lockedAssets.length > 0) {
      const params = new URLSearchParams();
      lockedAssets.forEach(assetId => {
        params.append('lockedAssets', assetId);
      });
      avatarCreationUrl += `&${params.toString()}`;
    }
    
    console.log('Opening ReadyPlayerMe with URL:', avatarCreationUrl);
    
    // Set session storage to track ReadyPlayerMe session
    sessionStorage.setItem('readyPlayerMeSession', 'active');
    
    // Open in a new window/tab
    const newWindow = window.open(
      avatarCreationUrl,
      'ready-player-me-avatar-creator',
      'width=800,height=600,scrollbars=yes,resizable=yes'
    );
    
    if (!newWindow) {
      throw new Error('Failed to open avatar creator. Please allow popups for this site.');
    }
    
    // Focus the new window
    newWindow.focus();
    
    // Set up a listener for when the window closes or redirects
    const checkClosed = setInterval(() => {
      if (newWindow.closed) {
        clearInterval(checkClosed);
        console.log('ReadyPlayerMe window was closed');
        // Window was closed, user might have completed avatar creation
        // Check if we have callback parameters in the URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('callback') === 'readyplayer') {
          console.log('ReadyPlayerMe callback detected, reloading page to handle it');
          window.location.reload();
        } else {
          console.log('No ReadyPlayerMe callback detected, checking for avatar updates...');
          // Since ReadyPlayerMe didn't redirect, we'll manually check for avatar updates
          // by reloading the page to refresh the avatar data
          console.log('Reloading page to check for avatar updates...');
          window.location.reload();
        }
      }
    }, 1000);

    // Listen for ReadyPlayerMe iframe events
    const handleMessage = (event: MessageEvent) => {
      console.log('Message received from popup:', event.origin, event.data);
      
      // Check if message is from ReadyPlayerMe
      if (event.origin !== 'https://wakarunning.readyplayer.me') return;
      
      try {
        const json = JSON.parse(event.data);
        console.log('ReadyPlayerMe JSON event:', json);
        
        if (json.source === 'readyplayerme') {
          console.log('ReadyPlayerMe event received:', json);
          
          // Handle different ReadyPlayerMe events
          switch (json.eventName) {
            case 'v1.frame.ready':
              console.log('ReadyPlayerMe frame ready, subscribing to events');
              // Subscribe to all events
              newWindow.postMessage(
                JSON.stringify({
                  target: 'readyplayerme',
                  type: 'subscribe',
                  eventName: 'v1.**',
                }),
                '*'
              );
              break;
              
            case 'v1.avatar.exported':
              console.log('Avatar exported, closing window');
              console.log('Avatar URL:', json.data.url);
              console.log('Avatar ID:', json.data.id);
              
              // Store the avatar data for callback handling
              sessionStorage.setItem('readyPlayerMeAvatarData', JSON.stringify({
                avatarUrl: json.data.url,
                avatarId: json.data.id,
                timestamp: Date.now()
              }));
              
              newWindow.close();
              clearInterval(checkClosed);
              window.removeEventListener('message', handleMessage);
              window.location.reload();
              break;
              
            case 'v1.user.authorized':
              console.log('User authorized:', json.data);
              break;
              
            default:
              console.log('ReadyPlayerMe event:', json);
          }
        }
      } catch (error) {
        // Handle non-JSON messages
        console.log('Non-JSON message from ReadyPlayerMe:', event.data);
      }
    };
    
    window.addEventListener('message', handleMessage);
  }
}
